import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { createCheckout } from '../lib/api';
import { Button } from '../components/ui/button';
import { Check, Star, Zap, MapPin, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const plans = [
  {
    id: 'basic',
    name: 'Basic',
    price: 10,
    description: 'Perfect for getting started',
    features: [
      'Business listing in directory',
      'Contact information display',
      'Category placement',
      'Mobile-optimized display',
      'Monthly billing',
    ],
    cta: 'Get Started',
    featured: false,
  },
  {
    id: 'featured',
    name: 'Featured',
    price: 20,
    description: 'Maximum visibility for your business',
    features: [
      'Everything in Basic',
      'Priority placement at top',
      'Featured badge on listing',
      'Enhanced visibility',
      'Stand out from competitors',
    ],
    cta: 'Go Featured',
    featured: true,
  },
];

export default function Pricing() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(null);

  const handleSelectPlan = async (planId) => {
    if (!isAuthenticated) {
      toast.info('Please create an account first');
      navigate('/signup');
      return;
    }

    setLoading(planId);
    try {
      const originUrl = window.location.origin;
      const res = await createCheckout(planId, originUrl);
      
      // Redirect to Stripe checkout
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-16 noise-bg">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-semibold tracking-tight mb-4">
            Simple, Transparent Pricing
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Choose the plan that fits your business. Cancel anytime.
            All plans include a professional listing in Tucson's premier business directory.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-testid="pricing-cards">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`pricing-card relative rounded-2xl border p-8 ${
                plan.featured
                  ? 'border-primary bg-card featured'
                  : 'border-border bg-card/50'
              }`}
              data-testid={`plan-${plan.id}`}
            >
              {/* Featured Badge */}
              {plan.featured && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <div className="flex items-center gap-1 bg-primary text-primary-foreground text-xs font-bold px-4 py-1.5 rounded-full">
                    <Star className="w-3 h-3 fill-current" />
                    MOST POPULAR
                  </div>
                </div>
              )}

              {/* Plan Header */}
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-2">
                  {plan.featured ? (
                    <Zap className="w-5 h-5 text-primary" />
                  ) : (
                    <MapPin className="w-5 h-5 text-muted-foreground" />
                  )}
                  <h3 className="text-xl font-semibold">{plan.name}</h3>
                </div>
                <p className="text-muted-foreground text-sm">{plan.description}</p>
              </div>

              {/* Price */}
              <div className="mb-8">
                <div className="flex items-baseline gap-1">
                  <span className="text-5xl font-bold">${plan.price}</span>
                  <span className="text-muted-foreground">/month</span>
                </div>
              </div>

              {/* Features */}
              <ul className="space-y-4 mb-8">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                      plan.featured ? 'bg-primary/20 text-primary' : 'bg-secondary text-muted-foreground'
                    }`}>
                      <Check className="w-3 h-3" />
                    </div>
                    <span className="text-sm text-muted-foreground">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <Button
                onClick={() => handleSelectPlan(plan.id)}
                className={`w-full h-12 rounded-xl ${
                  plan.featured ? 'glow-amber' : ''
                }`}
                variant={plan.featured ? 'default' : 'outline'}
                disabled={loading !== null}
                data-testid={`select-${plan.id}`}
              >
                {loading === plan.id ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  plan.cta
                )}
              </Button>
            </div>
          ))}
        </div>

        {/* FAQ */}
        <div className="mt-20 max-w-2xl mx-auto">
          <h2 className="text-2xl font-semibold text-center mb-8">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            {[
              {
                q: 'When does my listing go live?',
                a: 'Your listing goes live immediately after successful payment. Featured listings appear at the top of search results.',
              },
              {
                q: 'Can I cancel anytime?',
                a: 'Yes! You can cancel your subscription at any time from your dashboard. Your listing will remain active until the end of your billing period.',
              },
              {
                q: 'What happens if payment fails?',
                a: 'If a payment fails, your listing will be temporarily unpublished. It will be automatically restored once payment is successful.',
              },
              {
                q: 'Can I upgrade my plan?',
                a: 'Yes! You can upgrade from Basic to Featured at any time. Just visit the pricing page and select the Featured plan.',
              },
            ].map((faq, i) => (
              <div key={i} className="bg-card/50 rounded-xl p-6 border border-border">
                <h4 className="font-medium mb-2">{faq.q}</h4>
                <p className="text-sm text-muted-foreground">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <p className="text-muted-foreground mb-4">
            Not ready to commit? Browse our directory first.
          </p>
          <Link to="/">
            <Button variant="outline" className="rounded-full">
              Browse Businesses
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
